import React, { lazy } from 'react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
export function ProductIntroduction() {
  const {
    ref,
    isVisible
  } = useScrollAnimation(0.1);
  return <section ref={ref} className={`relative w-full py-10 md:py-12 lg:py-20 px-6 md:px-12 lg:px-20 bg-gradient-to-b from-white to-brand-teal/5 border-b-2 border-brand-teal scroll-animate ${isVisible ? 'visible' : ''}`} data-id="element-142">
      {/* Background Image */}
      <div className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-15 lg:bg-fixed pointer-events-none" style={{
      backgroundImage: 'url("https://images.unsplash.com/photo-1509316975850-ff9c5deb0cd9?w=1600&q=80")'
    }} aria-hidden="true" data-id="element-143" />

      <div className="relative z-10 max-w-[900px] mx-auto" data-id="element-144">
        <h2 className="text-[22px] md:text-[26px] lg:text-[32px] font-bold text-brand-teal leading-[1.4] mb-8 text-center" data-id="element-145">
          From Understanding the Problem to Solving It: Here's What Actually
          Works
        </h2>

        {/* Part 1: Solution-Aware */}
        <div className="mb-8" data-id="element-146"></div>

        {/* Divider */}
        <div className="h-[2px] w-[100px] mx-auto my-8 bg-gradient-to-r from-transparent via-brand-teal to-transparent opacity-70 animate-pulse" data-id="element-147" />

        {/* Part 2: Product-Aware */}
        <div className="mb-8" data-id="element-148">
          <p className="text-[16px] text-brand-navy font-normal leading-[1.8] mb-4" data-id="element-149">
            Most families try one of two approaches after understanding the
            water chemistry problem:
          </p>

          <div className="space-y-4 mb-6" data-id="element-150">
            <p className="text-[16px] text-brand-navy font-normal leading-[1.8] bg-brand-orange/5 p-3 rounded border-l-2 border-brand-orange" data-id="element-151">
              <span className="font-semibold text-brand-orange" data-id="element-152">
                Approach 1 (The Wrong One):
              </span>{' '}
              They install another cheap filter (hoping this one is different).
              They're <strong data-id="element-153">disappointed within weeks</strong> when results
              don't match their expectations. They conclude 'filters just don't
              work.'
            </p>
            <p className="text-[16px] text-brand-navy font-normal leading-[1.8] bg-brand-teal/5 p-3 rounded border-l-2 border-brand-teal" data-id="element-154">
              <span className="font-semibold text-brand-teal" data-id="element-155">
                Approach 2 (The One That Works):
              </span>{' '}
              They install a system <strong data-id="element-156">specifically engineered</strong> for
              the shower environment—one that uses a mechanism designed to
              handle <strong data-id="element-157">hot, fast-moving water</strong>. This is where
              Vitalene's solution enters the picture.
            </p>
          </div>

          <h3 id="product-reveal" className="md:text-[24px] lg:text-[28px] font-bold text-brand-teal tracking-[-0.3px] mt-6 mb-4 text-[25px]" data-id="element-158">
            Introducing: Vitalene's Purified Showerhead
          </h3>

          <div className="my-6 rounded-lg overflow-hidden shadow-md" data-id="element-159">
            <img src="https://cdn.magicpatterns.com/uploads/rXyNaUtdbRMikvnin4Rozu/unnamed_(13).jpg" alt="Vitalene's Purified Showerhead with multi-stage filtration media for toddler itch relief" className="w-full h-auto object-cover" loading="lazy" data-id="element-160" />
          </div>

          <ul className="space-y-2 mb-6 text-[16px] text-brand-navy font-normal leading-[1.7]" data-id="element-161">
            <li className="relative pl-6 before:content-['●'] before:absolute before:left-0 before:text-brand-teal before:text-[10px] before:top-[6px]" data-id="element-162">
              KDF-55 media triggers a redox reaction (not absorption) that
              converts chlorine into harmless chloride in the microseconds water
              contacts the media
            </li>
            <li className="relative pl-6 before:content-['●'] before:absolute before:left-0 before:text-brand-teal before:text-[10px] before:top-[6px]" data-id="element-163">
              Calcium sulfite protects the KDF-55 from degradation
            </li>
            <li className="relative pl-6 before:content-['●'] before:absolute before:left-0 before:text-brand-teal before:text-[10px] before:top-[6px]" data-id="element-164">
              Multi-stage ceramic and carbon media removes hard water minerals
              that soap reacts with
            </li>
            <li className="relative pl-6 before:content-['●'] before:absolute before:left-0 before:text-brand-teal before:text-[10px] before:top-[6px]" data-id="element-165">
              Temperature-optimized design works BETTER in hot water (the
              opposite of cheap filters)
            </li>
          </ul>

          <div className="my-6 rounded-lg overflow-hidden shadow-md" data-id="element-166">
            <img src="https://cdn.magicpatterns.com/uploads/hKf7YcUajwXue8QZQqT48B/unnamed_(12).jpg" alt="Multi-stage precision filtration diagram showing KDF-55, Activated Alumina, Calcium Sulfite, Mineral and Alkaline Stone Beads, and Maifan Stone and GAC layers" className="w-full h-auto object-cover" loading="lazy" data-id="element-167" />
          </div>

          <p className="text-[16px] text-brand-navy font-normal leading-[1.8]" data-id="element-168">
            This isn't a premium-priced, over-engineered solution. It's the
            minimum-engineered solution required to work in shower conditions.
            It's the difference between a $20 filter that fails and a $50 filter
            that succeeds.
          </p>
        </div>

        <div className="my-6 rounded-lg overflow-hidden shadow-md" data-id="element-169">
          <img src="https://cdn.magicpatterns.com/uploads/crUmnnAWxFVVZ8ipSyg2pV/unnamed_(5).jpg" alt="Install in under 2 minutes - 4 simple steps: remove old head, connect purified head, secure bracket, enjoy pure water" className="w-full h-auto object-cover" loading="lazy" data-id="element-170" />
        </div>

        {/* Divider */}
        <div className="h-[2px] w-[100px] mx-auto my-8 bg-gradient-to-r from-transparent via-brand-teal to-transparent opacity-70 animate-pulse" data-id="element-171" />

        {/* Part 3: Most-Aware */}
        <div data-id="element-172">
          <h3 className="text-[20px] md:text-[24px] lg:text-[28px] font-bold text-brand-teal tracking-[-0.3px] mb-4" data-id="element-173">
            At this point, you know:
          </h3>

          <ul className="space-y-2 mb-8 text-[16px] text-brand-navy font-normal leading-[1.8]" data-id="element-174">
            <li className="flex items-start gap-2" data-id="element-175">
              <span className="text-brand-teal font-bold text-[16px]" data-id="element-176">✓</span>{' '}
              Your child's problem is water chemistry (not parenting, genetics,
              or products)
            </li>
            <li className="flex items-start gap-2" data-id="element-177">
              <span className="text-brand-teal font-bold text-[16px]" data-id="element-178">✓</span>{' '}
              The mechanism requires addressing both chlorine and minerals
            </li>
            <li className="flex items-start gap-2" data-id="element-179">
              <span className="text-brand-teal font-bold text-[16px]" data-id="element-180">✓</span>{' '}
              Cheap filters don't work because their mechanism is wrong
            </li>
            <li className="flex items-start gap-2" data-id="element-181">
              <span className="text-brand-teal font-bold text-[16px]" data-id="element-182">✓</span>{' '}
              Most parents see improvement within 7-10 days of starting water
              treatment
            </li>
            <li className="flex items-start gap-2" data-id="element-183">
              <span className="text-brand-teal font-bold text-[16px]" data-id="element-184">✓</span>{' '}
              Installation is 2 minutes (unscrew showerhead, twist on filter,
              rescrew)
            </li>
            <li className="flex items-start gap-2" data-id="element-185">
              <span className="text-brand-teal font-bold text-[16px]" data-id="element-186">✓</span>{' '}
              Replacement cartridges are $15-20 and last 6-8 months
            </li>
          </ul>

          <div className="my-6 rounded-lg overflow-hidden shadow-md" data-id="element-187">
            <img src="https://cdn.magicpatterns.com/uploads/kDhNoREqHWxLuQoqgxgD3x/unnamed_(10).jpg" alt="See results in 14 days - skin improvement timeline from Day 1 heavy irritation to Day 30 problem solved" className="w-full h-auto object-cover" loading="lazy" data-id="element-188" />
          </div>

          <p className="text-[16px] text-brand-navy font-normal leading-[1.8] mb-8" data-id="element-189">
            You're in the position most parents{' '}
            <strong data-id="element-190">wish they'd been in</strong> weeks ago:
            <strong data-id="element-191"> informed, ready to solve</strong>, and armed with mechanism
            understanding.
          </p>

          {/* Offer Box */}
          <div className="bg-gradient-to-b from-brand-orange/15 to-brand-orange/5 border-2 border-brand-orange rounded-lg p-4 md:p-5 lg:p-6 shadow-[0_4px_16px_rgba(230,126,34,0.12)] my-8" data-id="element-192">
            <p className="text-[16px] md:text-[16px] lg:text-[17px] text-brand-navy font-medium leading-[1.8] m-0" data-id="element-193">
              For Australian parents making this decision today, we're offering
              20% off Vitalene's Purified Shower Head. This isn't a limited-time
              flash sale. This is our to parents who've done the research.
            </p>
          </div>

          <div className="my-6 rounded-lg overflow-hidden shadow-md" data-id="element-194">
            <img src="https://cdn.magicpatterns.com/uploads/i9jUa8fQLAKruNjz8NedqP/unnamed_(7).jpg" alt="This is what relief looks like - happy toddler enjoying a peaceful shower with purified water" className="w-full h-auto object-cover" loading="lazy" data-id="element-195" />
          </div>

          {/* Avatar Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-10" data-id="element-196">
            <div className="bg-white border border-brand-goldLight border-t-4 border-t-brand-gold rounded-lg p-6 shadow-[0_2px_8px_rgba(0,0,0,0.06)] min-h-[120px] flex flex-col justify-center transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_8px_24px_rgba(0,0,0,0.12)] hover:border-t-brand-orange group" data-id="element-197">
              <div className="text-[14px] text-brand-gold font-semibold uppercase tracking-[1px] mb-3 group-hover:text-brand-orange transition-colors" data-id="element-198">
                Sarah Mitchell
              </div>
              <p className="text-[16px] text-brand-navy font-medium leading-[1.7] italic m-0" data-id="element-199">
                "You've done everything right. This is the one piece you were
                missing."
              </p>
            </div>

            <div className="bg-white border border-brand-goldLight border-t-4 border-t-brand-gold rounded-lg p-6 shadow-[0_2px_8px_rgba(0,0,0,0.06)] min-h-[120px] flex flex-col justify-center transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_8px_24px_rgba(0,0,0,0.12)] hover:border-t-brand-orange group" data-id="element-200">
              <div className="text-[14px] text-brand-gold font-semibold uppercase tracking-[1px] mb-3 group-hover:text-brand-orange transition-colors" data-id="element-201">
                Jessica Chen
              </div>
              <p className="text-[16px] text-brand-navy font-medium leading-[1.7] italic m-0" data-id="element-202">
                "This solves what products alone cannot. Your child's skin will
                thank you."
              </p>
            </div>

            <div className="bg-white border border-brand-goldLight border-t-4 border-t-brand-gold rounded-lg p-6 shadow-[0_2px_8px_rgba(0,0,0,0.06)] min-h-[120px] flex flex-col justify-center transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_8px_24px_rgba(0,0,0,0.12)] hover:border-t-brand-orange group" data-id="element-203">
              <div className="text-[14px] text-brand-gold font-semibold uppercase tracking-[1px] mb-3 group-hover:text-brand-orange transition-colors" data-id="element-204">
                Michelle Kowalski
              </div>
              <p className="text-[16px] text-brand-navy font-medium leading-[1.7] italic m-0" data-id="element-205">
                "This completes your health optimization approach. Water quality
                is foundational."
              </p>
            </div>
          </div>

          <div className="mt-8 rounded-lg overflow-hidden shadow-md" data-id="element-206">
            <img src="https://cdn.magicpatterns.com/uploads/p5m4kaBXvZrpuiEM8RxXvX/unnamed_(4).jpg" alt="Real Family Real Transformation - Before purified, installation day, and after purified showing a family's journey to healthier skin through purified water" className="w-full h-auto object-cover" loading="lazy" data-id="element-207" />
          </div>
        </div>
      </div>
    </section>;
}